/**
 * Disable Organisation pure helpers (unit-tested in isolation from the network)
 */

import { CampaignSearchItem, SearchFilter, SearchRequest } from './types';

export const ORGANIZATION_ID_FILTER_KEY = 'organizationId';
/** Org filter key used by the account search (admins of an organisation). */
export const ADMIN_ORGANIZATION_ID_FILTER_KEY = 'adminFilterOrganizationId';

/**
 * Build a `/search` request body filtered to a single organisation.
 *
 * `orgFilterKey` varies by endpoint (the account search uses
 * `adminFilterOrganizationId`). `valueAsArray` wraps the filter value in an
 * array, which the v3 search endpoints (e.g. donations) require.
 */
export const buildOrgSearchRequest = (
  orgId: number,
  options: {
    page?: number;
    pageSize?: number;
    extraFilters?: SearchFilter[];
    orderBy?: { key: string; direction: 'asc' | 'desc' };
    orgFilterKey?: string;
    valueAsArray?: boolean;
  } = {}
): SearchRequest => {
  const {
    page = 1,
    pageSize = 1,
    extraFilters = [],
    orderBy,
    orgFilterKey = ORGANIZATION_ID_FILTER_KEY,
    valueAsArray = false
  } = options;

  return {
    page,
    pageSize,
    resultType: 'Full',
    text: '',
    filters: [
      { key: orgFilterKey, operator: 'EqualTo', value: valueAsArray ? [orgId] : orgId },
      ...extraFilters
    ],
    ...(orderBy ? { orderBy } : {})
  };
};

/**
 * Sum the `raisedAmount` across a page of campaign search results.
 */
export const sumRaisedAmount = (list: CampaignSearchItem[] | undefined): number =>
  (list ?? []).reduce((total, campaign) => total + (campaign.raisedAmount ?? 0), 0);

/**
 * Parse a numeric organisation id from raw user input. Returns null when the
 * input is not a positive integer.
 */
export const parseOrgId = (raw: string): number | null => {
  const trimmed = (raw ?? '').trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  return value > 0 ? value : null;
};
